import React from 'react';
import { Building2, Wrench, Package, Sparkles, TreePine, Warehouse } from 'lucide-react';
import { propertyData, sectionImages } from '../../data/mock';

const StructuresSection = () => {
  const getIcon = (name) => {
    if (name.includes('Steel') || name.includes('Conditioned')) return Building2;
    if (name.includes('Workshop')) return Wrench;
    if (name.includes('Storage')) return Package;
    if (name.includes('Sauna') || name.includes('Treehouse')) return Sparkles;
    if (name.includes('Barn')) return TreePine;
    return Warehouse;
  };

  // Map structure names to their correct images
  // Image content analysis:
  // - conditionedSteelBuilding.jpg = Interior with BLACK CORVETTE (blue stripes)
  // - executiveWorkshopInterior.jpg = Post-frame building interior with car lift & tractor
  // - barnComplex.jpg = AERIAL view of red barn and outbuildings
  // - saunaBungalow.jpg = Cedar sauna interior with heater
  //
  // User's requested mapping:
  // - Conditioned Steel Building → post-frame interior
  // - Executive Workshop → Corvette interior  
  // - Storage Building → gallery image
  // - Sauna Bungalow → cedar sauna interior
  // - Treehouse Suite → gallery image
  // - Barn Complex → aerial of red barn
  const getStructureImage = (structureName) => {
    const imageMap = {
      'Conditioned Steel Building': sectionImages.executiveWorkshopInterior, // Post-frame with car lift
      'Executive Workshop': sectionImages.conditionedSteelBuilding, // Corvette interior
      'Barn Complex': sectionImages.barnComplex, // Aerial of red barn
      'Sauna Bungalow': sectionImages.saunaBungalow, // Cedar sauna interior
      'Storage Building': sectionImages.storageBuilding, // User-provided barn/storage image
      'Treehouse Suite': propertyData.images.gallery[50], // From gallery
    };
    
    return imageMap[structureName] || propertyData.images.gallery[10];
  };

  return (
    <section id="infrastructure" className="py-24 bg-gradient-to-b from-[#161b22] to-[#0d1117]" data-testid="structures-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Turnkey Infrastructure
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6">
            BUSINESS-SUPPORT & COLLECTOR INFRASTRUCTURE
          </h2>
          <p className="text-lg text-white/70 font-light">
            Six distinct structures ready for immediate use. Workshop, storage, wellness, and equestrian facilities — 
            all operational from day one.
          </p>
        </div>

        {/* Structures Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {propertyData.estateStructures.map((structure) => {
            const Icon = getIcon(structure.name);
            const structureImage = getStructureImage(structure.name);
            const structureKey = structure.name.toLowerCase().replace(/\s+/g, '-');
            
            return (
              <div
                key={structureKey}
                className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-amber-500/40 transition-all duration-500"
                data-testid={`structure-${structureKey}`}
              >
                {/* Structure Image */}
                <div className="h-48 overflow-hidden">
                  <img 
                    src={structureImage}
                    alt={structure.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-transparent to-transparent" />
                </div>
                
                <div className="p-6 relative">
                  <div className="absolute -top-10 left-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 border-4 border-[#0d1117] flex items-center justify-center shadow-xl">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <h3 className="text-xl font-semibold text-white mb-1">{structure.name}</h3>
                    <p className="text-amber-400 text-sm mb-4">{structure.size}</p>
                    <ul className="space-y-2">
                      {structure.features.map((feature) => (
                        <li key={`${structure.name}-${feature.slice(0, 20)}`} className="flex items-start text-white/60 text-sm">
                          <span className="text-amber-400 mr-2">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Infrastructure Highlight */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-4 bg-amber-500/10 border border-amber-500/30 rounded-full px-8 py-4">
            <span className="text-amber-400 font-semibold">TRUE OPERATIONAL ESTATE</span>
            <span className="text-white/60">—</span>
            <span className="text-white/70 font-light">Infrastructure stack separates it from typical acreage residences</span>
          </div>
        </div>

        {/* Zoning Disclaimer */}
        <p className="text-center text-white/40 text-xs mt-8">
          Business and operational possibilities subject to applicable zoning and governmental requirements.
        </p>
      </div>
    </section>
  );
};

export default StructuresSection;
